import { useState } from 'react';
import { LoopSelect, loopInputStyle, fieldLabelStyle } from './LoopSelect.js';

/** First day Diff Vader produced data — mirrors SCORECARD_DATA_START in the
 *  API. Used only as the date inputs' `min`; the server clamps authoritatively. */
export const DATA_START = '2026-05-13';

export const PERIOD_PRESETS = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
] as const;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const COMPLETE_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A native date input reports '' while a value is only partly typed —
 *  never let that (or any other incomplete text) reach the URL. */
function isCompleteDate(v: string): boolean {
  return COMPLETE_DATE.test(v);
}

function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + n * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Which preset, if any, a concrete range corresponds to. A range is a preset
 *  only when it ends today AND spans exactly that many days — otherwise the
 *  dropdown would claim "Last 30 days" for a historical 30-day slice. */
export function presetForRange(from: string, to: string, today: string): string {
  if (to !== today) return 'custom';
  const match = PERIOD_PRESETS.find(p => addDays(today, -(p.days - 1)) === from);
  return match ? match.value : 'custom';
}

interface PeriodPickerProps {
  from: string;
  to: string;
  today: string;
  onChange: (next: { from: string; to: string }) => void;
}

export function PeriodPicker({ from, to, today, onChange }: PeriodPickerProps) {
  // Whether the inputs are expanded is presentational, so it stays local rather
  // than going in the URL: a shared link should describe the range, not the
  // sender's control layout, and a `period=custom` param could contradict the
  // range it travels with. It has to be remembered, though — choosing custom
  // re-emits the range unchanged, so the props come back identical and a purely
  // derived flag would never flip.
  const [forcedCustom, setForcedCustom] = useState(false);

  // The last range *this component* handed to onChange. Lets the
  // prop-change reaction below tell its own echo apart from a genuine
  // external change (e.g. browser Back). Without this, committing a draft
  // that happens to land on a preset-shaped range (see commitDraft) would
  // look identical to an external navigation and incorrectly collapse the
  // inputs mid-edit. State rather than a ref: react-hooks/refs forbids
  // reading a ref's `.current` during render, and this has to be read below
  // in the same render-time prop-change reaction as everything else here.
  const [lastEmitted, setLastEmitted] = useState<{ from: string; to: string } | null>(null);

  function emit(next: { from: string; to: string }) {
    setLastEmitted(next);
    onChange(next);
  }

  // Local drafts for the two date inputs (Defect A: a native date input
  // fires `change` on every arrow-key tick, so wiring that straight to
  // onChange rewrote the URL — and refetched the whole scorecard — on every
  // tick before the user had chosen anything). Typing/arrowing updates only
  // these; onChange is only called from commitDraft, on blur/Enter.
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  // Re-seed the drafts and clear the override when from/to change from
  // OUTSIDE this component — e.g. browser Back restoring the URL to a
  // preset while the picker is still forced open on Custom. `seenRange` is
  // the from/to this render is reacting to; adjusting state here (during
  // render, rather than in an effect — react-hooks/set-state-in-effect
  // flags the effect form as a cascading-render risk) is the
  // React-documented pattern for resetting state when a prop changes.
  // Keyed on [from, to] alone and deliberately not on `today` or the derived
  // preset: the user's own "Custom range…" click (handlePreset) re-emits
  // the *same* from/to, so `seenRange` doesn't change and this won't fire
  // then — the override survives exactly when it's supposed to. Confirmed
  // by the controlled-parent tests below.
  //
  // `isOwnEcho` guards the override clear specifically: a change that
  // matches what we last emitted ourselves is this component's own commit
  // coming back through the controlled parent, never an external
  // navigation, so it must not clear forcedCustom — even when the committed
  // range happens to be preset-shaped (commitDraft already forces true in
  // that case; this keeps the two mechanisms consistent instead of racing).
  const [seenRange, setSeenRange] = useState({ from, to });
  if (seenRange.from !== from || seenRange.to !== to) {
    setSeenRange({ from, to });
    setDraftFrom(from);
    setDraftTo(to);
    const isOwnEcho = lastEmitted?.from === from && lastEmitted?.to === to;
    if (!isOwnEcho && presetForRange(from, to, today) !== 'custom') {
      setForcedCustom(false);
    }
    // lastEmitted is a one-shot marker: it exists only to tell this render's
    // prop change apart from an external one, not to remember every range
    // this component has ever emitted. Clear it once evaluated — whether it
    // matched (consumed) or not (stale) — so a later external navigation
    // back to the same range isn't mistaken for another own echo.
    setLastEmitted(null);
  }

  const derived = presetForRange(from, to, today);
  const isCustom = forcedCustom || derived === 'custom';
  const preset = isCustom ? 'custom' : derived;

  function handlePreset(value: string) {
    if (value === 'custom') {
      // Keep the range as-is and just reveal the inputs, so switching to
      // custom never silently moves the data the user is looking at.
      setForcedCustom(true);
      emit({ from, to });
      return;
    }
    const p = PERIOD_PRESETS.find(x => x.value === value);
    if (!p) return;
    setForcedCustom(false);
    emit({ from: addDays(today, -(p.days - 1)), to: today });
  }

  // Commits a drafted date to the parent — called on blur and Enter, never
  // on change. `setForcedCustom(true)` here matters because the date inputs
  // are only ever rendered while isCustom is already true: any commit
  // originating from them means the user is actively working the custom UI,
  // even if the resulting range happens to coincide with a preset shape
  // (Defect B) — that must not snap the inputs shut mid-edit.
  function commitDraft(next: { from: string; to: string }) {
    if (!isCompleteDate(next.from) || !isCompleteDate(next.to)) return;
    if (next.from === from && next.to === to) return;
    setForcedCustom(true);
    emit(next);
  }

  return (
    <>
      <LoopSelect
        id="scorecard-period-select"
        label="Period"
        value={preset}
        onChange={handlePreset}
        options={[
          ...PERIOD_PRESETS.map(p => ({ value: p.value, label: p.label })),
          { value: 'custom', label: 'Custom range…' },
        ]}
      />
      {isCustom && (
        <>
          <div>
            <label htmlFor="scorecard-from" style={fieldLabelStyle}>From</label>
            <input
              id="scorecard-from" type="date" style={loopInputStyle}
              value={draftFrom} min={DATA_START} max={today}
              onChange={e => setDraftFrom(e.target.value)}
              onBlur={() => commitDraft({ from: draftFrom, to: draftTo })}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraft({ from: draftFrom, to: draftTo });
                }
              }}
            />
          </div>
          <div>
            <label htmlFor="scorecard-to" style={fieldLabelStyle}>To</label>
            <input
              id="scorecard-to" type="date" style={loopInputStyle}
              value={draftTo} min={DATA_START} max={today}
              onChange={e => setDraftTo(e.target.value)}
              onBlur={() => commitDraft({ from: draftFrom, to: draftTo })}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraft({ from: draftFrom, to: draftTo });
                }
              }}
            />
          </div>
        </>
      )}
    </>
  );
}
